import React, { useState, useEffect } from 'react';
import {
  Truck,
  RefreshCw,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  Store,
  Package,
  DollarSign,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  Calendar,
  MapPin
} from 'lucide-react';
import './ChowdeckManagement.css';

// NGN formatter
const formatNGN = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const ChowdeckManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected merchant for details
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Filters
  const [merchantFilters, setMerchantFilters] = useState({
    search: ''
  });
  const [orderFilters, setOrderFilters] = useState({
    status: '',
    dispatched: '',
    startDate: '',
    endDate: ''
  });

  // Pagination
  const [merchantPage, setMerchantPage] = useState(1);
  const [merchantTotalPages, setMerchantTotalPages] = useState(1);
  const [orderPage, setOrderPage] = useState(1);
  const [orderTotalPages, setOrderTotalPages] = useState(1);

  // Load stats
  const loadStats = async () => {
    try {
      const response = await fetch('/api/admin/chowdeck/stats', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  // Load merchants
  const loadMerchants = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: merchantPage.toString(),
        limit: '20'
      });
      if (merchantFilters.search) params.append('search', merchantFilters.search);

      const response = await fetch(`/api/admin/chowdeck/merchants?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setMerchants(data.merchants || []);
        setMerchantTotalPages(data.pagination?.totalPages || 1);
      } else {
        setError(data.error || 'Failed to load merchants');
      }
    } catch (err) {
      setError('Failed to load merchants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load orders
  const loadOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: orderPage.toString(),
        limit: '20'
      });
      if (orderFilters.status) params.append('status', orderFilters.status);
      if (orderFilters.dispatched) params.append('dispatched', orderFilters.dispatched);
      if (orderFilters.startDate) params.append('startDate', orderFilters.startDate);
      if (orderFilters.endDate) params.append('endDate', orderFilters.endDate);

      const response = await fetch(`/api/admin/chowdeck/orders?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setOrders(data.orders || []);
        setOrderTotalPages(data.pagination?.totalPages || 1);
      } else {
        setError(data.error || 'Failed to load orders');
      }
    } catch (err) {
      setError('Failed to load orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load merchant details
  const loadMerchantDetails = async (merchantId) => {
    try {
      setLoadingDetails(true);
      const response = await fetch(`/api/admin/chowdeck/merchants/${merchantId}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setMerchantDetails(data);
      }
    } catch (err) {
      console.error('Failed to load merchant details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadStats();
  }, []);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'merchants') {
      loadMerchants();
    } else if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab, merchantPage, merchantFilters, orderPage, orderFilters]);

  const openMerchantDetails = (merchant) => {
    setSelectedMerchant(merchant);
    setShowDetailsModal(true);
    loadMerchantDetails(merchant.merchantId);
  };

  return (
    <div className="cdm-container">
      <div className="cdm-header">
        <div className="cdm-header-left">
          <Truck className="cdm-header-icon" />
          <div>
            <h1>Chowdeck Delivery</h1>
            <p>Monitor Chowdeck delivery usage and merchants</p>
          </div>
        </div>
        <button className="cdm-btn cdm-btn-secondary" onClick={() => { loadStats(); if (activeTab === 'merchants') loadMerchants(); else loadOrders(); }}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Messages */}
      {error && <div className="cdm-message cdm-message-error">{error}</div>}

      {/* Stats Cards */}
      {stats && (
        <div className="cdm-stats-grid">
          <div className="cdm-stat-card">
            <div className="cdm-stat-icon cdm-stat-icon-blue">
              <Store size={20} />
            </div>
            <div className="cdm-stat-content">
              <span className="cdm-stat-label">Enabled Merchants</span>
              <span className="cdm-stat-value">{stats.enabledMerchants || 0}</span>
            </div>
          </div>
          <div className="cdm-stat-card">
            <div className="cdm-stat-icon cdm-stat-icon-green">
              <Package size={20} />
            </div>
            <div className="cdm-stat-content">
              <span className="cdm-stat-label">Total Orders</span>
              <span className="cdm-stat-value">{stats.orders?.total || 0}</span>
            </div>
          </div>
          <div className="cdm-stat-card">
            <div className="cdm-stat-icon cdm-stat-icon-purple">
              <Truck size={20} />
            </div>
            <div className="cdm-stat-content">
              <span className="cdm-stat-label">Dispatched</span>
              <span className="cdm-stat-value">{stats.orders?.dispatched || 0}</span>
            </div>
          </div>
          <div className="cdm-stat-card">
            <div className="cdm-stat-icon cdm-stat-icon-orange">
              <DollarSign size={20} />
            </div>
            <div className="cdm-stat-content">
              <span className="cdm-stat-label">Fees Collected</span>
              <span className="cdm-stat-value">{formatNGN(stats.fees?.totalCollected)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="cdm-tabs">
        <button
          className={`cdm-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`cdm-tab ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          Merchants
        </button>
        <button
          className={`cdm-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Orders
        </button>
      </div>

      {/* Tab Content */}
      <div className="cdm-tab-content">
        {activeTab === 'overview' && stats && (
          <div className="cdm-overview">
            {/* Recent Orders Chart Placeholder */}
            <div className="cdm-section">
              <h3>Orders (Last 30 Days)</h3>
              {stats.recentOrders && stats.recentOrders.length > 0 ? (
                <div className="cdm-chart-placeholder">
                  <div className="cdm-recent-orders">
                    {stats.recentOrders.slice(0, 7).map((day, idx) => (
                      <div key={idx} className="cdm-day-stat">
                        <span className="cdm-day-date">
                          {new Date(day.date).toLocaleDateString('en-NG', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="cdm-day-orders">{day.orders} orders</span>
                        <span className="cdm-day-fees">{formatNGN(day.fees)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="cdm-no-data">No order data available</p>
              )}
            </div>

            {/* Top Merchants */}
            <div className="cdm-section">
              <h3>Top Merchants by Dispatch Count</h3>
              {stats.topMerchants && stats.topMerchants.length > 0 ? (
                <div className="cdm-top-merchants">
                  {stats.topMerchants.map((m, idx) => (
                    <div key={idx} className="cdm-top-merchant">
                      <span className="cdm-rank">#{idx + 1}</span>
                      <div className="cdm-merchant-info">
                        <span className="cdm-merchant-name">{m.storeName || m.email}</span>
                        <span className="cdm-merchant-email">{m.email}</span>
                      </div>
                      <div className="cdm-merchant-stats">
                        <span>{m.dispatchCount} dispatches</span>
                        <span>{formatNGN(m.totalFees)} fees</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="cdm-no-data">No merchant data available</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'merchants' && (
          <div className="cdm-merchants">
            {/* Filters */}
            <div className="cdm-filters">
              <div className="cdm-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by email or store name..."
                  value={merchantFilters.search}
                  onChange={(e) => {
                    setMerchantFilters(f => ({ ...f, search: e.target.value }));
                    setMerchantPage(1);
                  }}
                />
              </div>
            </div>

            {/* Merchants Table */}
            <div className="cdm-table-container">
              {loading ? (
                <div className="cdm-loading">
                  <Loader className="cdm-spinner" size={24} />
                  Loading merchants...
                </div>
              ) : merchants.length === 0 ? (
                <div className="cdm-empty">
                  <Store size={48} />
                  <p>No merchants with Chowdeck enabled</p>
                </div>
              ) : (
                <table className="cdm-table">
                  <thead>
                    <tr>
                      <th>Store</th>
                      <th>Email</th>
                      <th>Tier</th>
                      <th>Wallet Balance</th>
                      <th>Wallet Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merchants.map((m) => (
                      <tr key={m.storeId}>
                        <td className="cdm-store-name">{m.storeName}</td>
                        <td>{m.email}</td>
                        <td>
                          <span className={`cdm-tier cdm-tier-${(m.merchantTier || 'free').toLowerCase()}`}>
                            {m.merchantTier || 'FREE'}
                          </span>
                        </td>
                        <td>{formatNGN(m.wallet?.balance)}</td>
                        <td>
                          <span className={`cdm-status cdm-status-${(m.wallet?.status || 'active').toLowerCase()}`}>
                            {m.wallet?.status || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="cdm-action-btn"
                            onClick={() => openMerchantDetails(m)}
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {merchantTotalPages > 1 && (
              <div className="cdm-pagination">
                <button
                  onClick={() => setMerchantPage(p => Math.max(1, p - 1))}
                  disabled={merchantPage === 1}
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <span>Page {merchantPage} of {merchantTotalPages}</span>
                <button
                  onClick={() => setMerchantPage(p => Math.min(merchantTotalPages, p + 1))}
                  disabled={merchantPage === merchantTotalPages}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="cdm-orders">
            {/* Filters */}
            <div className="cdm-filters">
              <select
                value={orderFilters.status}
                onChange={(e) => {
                  setOrderFilters(f => ({ ...f, status: e.target.value }));
                  setOrderPage(1);
                }}
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="SHIPPED">Shipped</option>
                <option value="DELIVERED">Delivered</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
              <select
                value={orderFilters.dispatched}
                onChange={(e) => {
                  setOrderFilters(f => ({ ...f, dispatched: e.target.value }));
                  setOrderPage(1);
                }}
              >
                <option value="">All Dispatch</option>
                <option value="true">Dispatched</option>
                <option value="false">Not Dispatched</option>
              </select>
              <input
                type="date"
                value={orderFilters.startDate}
                onChange={(e) => {
                  setOrderFilters(f => ({ ...f, startDate: e.target.value }));
                  setOrderPage(1);
                }}
                placeholder="Start Date"
              />
              <input
                type="date"
                value={orderFilters.endDate}
                onChange={(e) => {
                  setOrderFilters(f => ({ ...f, endDate: e.target.value }));
                  setOrderPage(1);
                }}
                placeholder="End Date"
              />
            </div>

            {/* Orders Table */}
            <div className="cdm-table-container">
              {loading ? (
                <div className="cdm-loading">
                  <Loader className="cdm-spinner" size={24} />
                  Loading orders...
                </div>
              ) : orders.length === 0 ? (
                <div className="cdm-empty">
                  <Package size={48} />
                  <p>No Chowdeck orders found</p>
                </div>
              ) : (
                <table className="cdm-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Store</th>
                      <th>Status</th>
                      <th>Dispatched</th>
                      <th>Shipping Fee</th>
                      <th>Total</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id}>
                        <td className="cdm-order-number">{o.orderNumber}</td>
                        <td>{o.storeName || 'N/A'}</td>
                        <td>
                          <span className={`cdm-status cdm-status-${(o.status || '').toLowerCase()}`}>
                            {o.status}
                          </span>
                        </td>
                        <td>
                          {o.isDispatched ? (
                            <span className="cdm-dispatched yes">
                              <CheckCircle size={14} /> Yes
                            </span>
                          ) : (
                            <span className="cdm-dispatched no">
                              <Clock size={14} /> No
                            </span>
                          )}
                        </td>
                        <td>{formatNGN(o.shippingFee)}</td>
                        <td>{formatNGN(o.total)}</td>
                        <td>
                          {new Date(o.createdAt).toLocaleDateString('en-NG', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {orderTotalPages > 1 && (
              <div className="cdm-pagination">
                <button
                  onClick={() => setOrderPage(p => Math.max(1, p - 1))}
                  disabled={orderPage === 1}
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <span>Page {orderPage} of {orderTotalPages}</span>
                <button
                  onClick={() => setOrderPage(p => Math.min(orderTotalPages, p + 1))}
                  disabled={orderPage === orderTotalPages}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Merchant Details Modal */}
      {showDetailsModal && (
        <div className="cdm-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="cdm-modal cdm-modal-large" onClick={e => e.stopPropagation()}>
            <div className="cdm-modal-header">
              <h2>Merchant Chowdeck Details</h2>
              <button onClick={() => setShowDetailsModal(false)}>&times;</button>
            </div>
            <div className="cdm-modal-body">
              {loadingDetails ? (
                <div className="cdm-loading">
                  <Loader className="cdm-spinner" size={24} />
                  Loading...
                </div>
              ) : merchantDetails ? (
                <>
                  <div className="cdm-detail-section">
                    <h4>Merchant Info</h4>
                    <div className="cdm-detail-grid">
                      <div className="cdm-detail-item">
                        <label>Store</label>
                        <span>{merchantDetails.merchant?.store?.name || 'N/A'}</span>
                      </div>
                      <div className="cdm-detail-item">
                        <label>Email</label>
                        <span>{merchantDetails.merchant?.email}</span>
                      </div>
                      <div className="cdm-detail-item">
                        <label>Tier</label>
                        <span>{merchantDetails.merchant?.merchantTier || 'FREE'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="cdm-detail-section">
                    <h4>Wallet Info</h4>
                    {merchantDetails.merchant?.wallet ? (
                      <div className="cdm-detail-grid">
                        <div className="cdm-detail-item">
                          <label>Wallet Ref</label>
                          <span>{merchantDetails.merchant.wallet.walletRef}</span>
                        </div>
                        <div className="cdm-detail-item">
                          <label>Balance</label>
                          <span className="cdm-balance">{formatNGN(merchantDetails.merchant.wallet.balance)}</span>
                        </div>
                        <div className="cdm-detail-item">
                          <label>Status</label>
                          <span className={`cdm-status cdm-status-${merchantDetails.merchant.wallet.status.toLowerCase()}`}>
                            {merchantDetails.merchant.wallet.status}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p>No wallet found</p>
                    )}
                  </div>

                  <div className="cdm-detail-section">
                    <h4>Chowdeck Stats</h4>
                    <div className="cdm-detail-grid">
                      <div className="cdm-detail-item">
                        <label>Total Orders</label>
                        <span>{merchantDetails.stats?.totalOrders || 0}</span>
                      </div>
                      <div className="cdm-detail-item">
                        <label>Total Fees</label>
                        <span>{formatNGN(merchantDetails.stats?.totalFees)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="cdm-detail-section">
                    <h4>Recent Orders</h4>
                    {merchantDetails.recentOrders && merchantDetails.recentOrders.length > 0 ? (
                      <div className="cdm-recent-list">
                        {merchantDetails.recentOrders.slice(0, 5).map((o) => (
                          <div key={o.id} className="cdm-recent-item">
                            <span className="cdm-recent-order">{o.orderNumber}</span>
                            <span className={`cdm-status cdm-status-${o.status.toLowerCase()}`}>{o.status}</span>
                            <span>{formatNGN(o.shippingFee)}</span>
                            <span>{new Date(o.createdAt).toLocaleDateString('en-NG')}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="cdm-no-data">No recent orders</p>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChowdeckManagement;
